/** Same as CCParsedMessage: image type by URL extension. */
const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
/** Match ![alt](url) or [label](url) anywhere in body. */
const ATTACHMENT_REGEX =
  /!\[[^\]]*\]\(([^)]+)\)|\[(?:[^\]]+)\]\(([^)]+)\)/gi;

/** Same "Sent an image" copy as the attachment path below, for html <img> bodies. */
const SENT_AN_IMAGE = 'Sent an image';
const HTML_IMG_TAG = /<img\b/i;

/**
 * Decode HTML entities. Named entities are decoded first, numeric entities
 * (decimal + hex) next, and `&amp;` LAST -- decoding it earlier could
 * re-decode an already-escaped entity (e.g. a literal "&lt;" in the source
 * text) a second time.
 */
function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16)),
    )
    .replace(/&amp;/g, '&');
}

/**
 * Strip HTML tags/entities from an html-format message body so it can be
 * shown as a plain one-line preview (e.g. channel-list last message).
 * An image-only body (no other text) falls back to the same "Sent an
 * image" copy the markdown attachment path below uses, instead of an
 * empty/garbled preview.
 */
export function stripHtmlForPreview(s: string | null | undefined): string {
  if (!s) return '';
  const stripped = decodeHtmlEntities(s.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  if (!stripped && HTML_IMG_TAG.test(s)) {
    return SENT_AN_IMAGE;
  }
  return stripped;
}

/**
 * Preview text for inbox/chat last message: show "Sent an image" / "Sent an attachment"
 * instead of raw markdown; otherwise truncate body.
 */
export function lastMessagePreview(
  body: string | null | undefined,
  maxLen = 25,
): string {
  if (body == null || body === '') return '—';
  let hasImage = false;
  let hasAttachment = false;
  let m: RegExpExecArray | null;
  ATTACHMENT_REGEX.lastIndex = 0;
  while ((m = ATTACHMENT_REGEX.exec(body)) !== null) {
    hasAttachment = true;
    const url = (m[1] ?? m[2]) ?? '';
    if (IMAGE_EXTENSIONS.test(url)) {
      hasImage = true;
      break;
    }
  }
  if (hasImage) return SENT_AN_IMAGE;
  if (hasAttachment) return 'Sent an attachment';
  return body.length > maxLen ? `${body.slice(0, maxLen)}...` : body;
}
